import PropTypes from "prop-types";
import React, { useState } from "react";
import { Accordion, Card } from "react-bootstrap";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import { CollapseButton, ExpandButton } from "../../../../assets/icons";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import { useSortableData } from "../../dashboard-util/dashboard-utils";
import ArrowDown from "../../../../assets/icons/ArrowDown.svg";
import ArrowUp from "../../../../assets/icons/ArrowUp.svg";
import ArrowHover from "../../../../assets/icons/ArrowHover.svg";

const L2LClientOpportunityTable = ({ clientOpportunityData }) => {
  const [expandedClients, setExpandedClients] = useState([]);
  const { items, requestSort, getArrowDirectionFor } = useSortableData(clientOpportunityData);

  const isAllExpanded = expandedClients.length === clientOpportunityData.length;

  const handleAccordionToggle = (clientName) => {
    setExpandedClients((prev) =>
      prev.includes(clientName)
        ? prev.filter((name) => name !== clientName)
        : [...prev, clientName]
    );
  };

  const toggleAll = () => {
    setExpandedClients(isAllExpanded ? [] : clientOpportunityData.map((client) => client.ClientName));
  };

  const renderTooltipContent = (text, maxLength = 40) =>
    text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  const clientTableHeaders = [
    { label: "Client Name", key: "ClientName" },
    { label: "Total Estimate ARR", key: "ARRAmount" },
    { label: "Total Estimate Services Revenue", key: "ServicesAmount" },
    { label: "Weighted Total Revenue", key: "WeighedAmount" },
    { label: "", key: "Actions" },
  ];

  const opportunityTableHeaders = [
    { label: "Opportunity Name", key: "OpportunityName" },
    { label: "Deal Type", key: "DealType" },
    { label: "Revenue Type", key: "RevenueType" },
    { label: "Status", key: "Status" },
    { label: "Estimate ARR", key: "ARRAmount" },
    { label: "Estimate Services Revenue", key: "ServicesAmount" },
    { label: "Weighted Total Revenue", key: "WeighedAmount" },
  ];

  const renderClientRow = (client) => (
    <tr key={client.ClientName} style={{ background: "#FFFFFF" }}>
      {clientTableHeaders.map((header) => {
        if (header.key === "ClientName") {
          return (
            <td key={header.key} className="col-md-1 col-sm-1 mt-2" style={{ textAlign: "start" }}>
              <FluentTooltip
                target={<span>{renderTooltipContent(client[header.key])}</span>}
                text={client[header.key]}
              />
            </td>
          );
        }
        if (header.key === "Actions") {
          return (
            <td key={header.key} className="col-md-1 col-sm-1 mt-2">
              <FluentButton
                appearance="none"
                onClick={() => handleAccordionToggle(client.ClientName)}
                icon={
                  expandedClients.includes(client.ClientName) ? (
                    <FluentUiIcon name={"ChevronUpRegular"} color={"#0F6CBD"} size={20} />
                  ) : (
                    <FluentUiIcon name={"ChevronDownRegular"} color={"#0F6CBD"} size={20} />
                  )
                }
              />
            </td>
          );
        }
        return (
          <td key={header.key} className="col-md-1 col-sm-1 mt-2" style={{ textAlign: "start" }}>
            ${client[header.key].toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </td>
        );
      })}
    </tr>
  );

  const renderOpportunityRows = (opportunities) =>
    opportunities.map((opportunity, index) => (
      <tr key={index} style={{ textAlign: "start", background: "#FAF9F8" }}>
        {opportunityTableHeaders.map((header) => {
          if (header.key === "OpportunityName") {
            return (
              <td key={header.key} className="col-md-1 col-sm-1 mt-2">
                <FluentTooltip
                  target={<span>{renderTooltipContent(opportunity[header.key])}</span>}
                  text={opportunity[header.key]}
                />
              </td>
            );
          }
          return (
            <td key={header.key} className="col-md-1 col-sm-1 mt-2">
              {header.key.includes("Amount")
                ? `$${opportunity[header.key].toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : opportunity[header.key] || "-"}
            </td>
          );
        })}
      </tr>
    ));

  return (
    <div className="L2LDashboardContainer">
      <div className="DashboardTableHeader">
        <div className="DashboardTableTitle">
          <span className="L2LDashboardHeaderFont">Clients/Projects Overview</span>
          <button
            type="button"
            className="expand-collapse-button"
            onClick={toggleAll}
            aria-label={isAllExpanded ? "Collapse all rows" : "Expand all rows"}
          >
            <FluentTooltip
              target={
                <img
                  src={isAllExpanded ? CollapseButton : ExpandButton}
                  alt={isAllExpanded ? "Collapse All" : "Expand All"}
                />
              }
              text={isAllExpanded ? "Collapse all rows" : "Expand all rows"}
            />
          </button>
        </div>
      </div>
      <table>
        <thead className="L2LHeader">
        <tr className="tableColumnTitles tableHeader">
      {clientTableHeaders.map((header) => (
        <th
          key={header.key}
          className={
            header.key === "Actions"
              ? "col-md-1 col-sm-1" 
              : "col-md-1 col-sm-1 textEllipsis headerSorted hoverHeader" 
          }
        >
          {header.key !== "Actions" ? (
            <div
              onClick={() => requestSort(header.key)}
              className={"sortedCenter L2LSortedHeaders"}
            >
              <div className="containerTextSorted L2LSortedHeaders">
                <p className="textSorted">{header.label}</p>
              </div>
              {getArrowDirectionFor(header.key, "descending") && (
                <img className="arrowDirection" alt="Arrow down" src={ArrowDown} />
              )}
              {getArrowDirectionFor(header.key, "ascending") && (
                <img className="arrowDirection" alt="Arrow up" src={ArrowUp} />
              )}
              {getArrowDirectionFor(header.key) && (
                <img className="arrowDirectionHover" alt="Arrow sort" src={ArrowHover} />
              )}
            </div>
          ) : (
            <span>{header.label}</span> 
          )}
        </th>
      ))}
    </tr>
        </thead>
        <tbody className="L2LTableBody">
          {items.map((client) => (
            <React.Fragment key={client.ClientName}>
              {renderClientRow(client)}
              {expandedClients.includes(client.ClientName) && (
                <tr>
                  <td colSpan="5">
                    <Accordion activeKey={expandedClients.includes(client.ClientName) ? client.ClientName : null}>
                      <Card>
                        <Accordion.Collapse eventKey={client.ClientName}>
                          <Card.Body>
                            <table className="opportunity-table">
                              <thead className="L2LHeader">
                                <tr className="tableColumnTitles tableHeader" style={{ background: "#DDDDDD" }}>
                                  {opportunityTableHeaders.map((header) => (
                                    <th key={header.key} className="col-md-1 col-sm-1 textEllipsis headerSorted">
                                      {header.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="tableBody L2LAccordionBody">
                                {renderOpportunityRows(client.Opportunities)}
                              </tbody>
                            </table>
                          </Card.Body>
                        </Accordion.Collapse>
                      </Card>
                    </Accordion>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

L2LClientOpportunityTable.propTypes = {
  clientOpportunityData: PropTypes.arrayOf(
    PropTypes.shape({
      ClientName: PropTypes.string.isRequired,
      ARRAmount: PropTypes.number.isRequired,
      ServicesAmount: PropTypes.number.isRequired,
      WeighedAmount: PropTypes.number.isRequired,
      Opportunities: PropTypes.arrayOf(
        PropTypes.shape({
          OpportunityName: PropTypes.string.isRequired,
          DealType: PropTypes.string,
          RevenueType: PropTypes.string,
          Status: PropTypes.string.isRequired,
          ARRAmount: PropTypes.number.isRequired,
          ServicesAmount: PropTypes.number.isRequired,
          WeighedAmount: PropTypes.number.isRequired,
        }).isRequired
      ).isRequired,
    }).isRequired
  ).isRequired,
};

export default L2LClientOpportunityTable;
